import React from 'react';
import { THEME } from '../../constants/theme';

const ACCENT = (THEME && THEME.secondary) || '#4f46e5';

const Icon = ({ children }) => (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        {children}
    </svg>
);

const ICONS = {
    purchase: (
        <>
            <circle cx="9" cy="20" r="1.3" fill="#fff" stroke="none" />
            <circle cx="17" cy="20" r="1.3" fill="#fff" stroke="none" />
            <path d="M2.5 3h2.2l1.9 10.6a2 2 0 0 0 2 1.6h7.4a2 2 0 0 0 2-1.6L20 7.5H6" />
        </>
    ),
    sale: (
        <>
            <path d="M3 13h9l2.5-4H21l-2 7H6L3.5 5H2" />
            <circle cx="8" cy="19" r="1.3" fill="#fff" stroke="none" />
            <circle cx="17" cy="19" r="1.3" fill="#fff" stroke="none" />
        </>
    ),
    income: (
        <>
            <rect x="3" y="7.5" width="18" height="11.5" rx="1.5" />
            <path d="M3 11h18" />
            <circle cx="7" cy="15" r="1" fill="#fff" stroke="none" />
            <path d="M8 3.5h8l2 4H6l2-4Z" />
        </>
    ),
    docIn: (
        <>
            <path d="M4 3.5h8.5l3.5 3.5v13.5H4Z" />
            <path d="M12.5 3.5v3.5H16" />
            <circle cx="9" cy="16" r="2.7" />
            <path d="M9 14.6v2.8M7.6 16H9" />
        </>
    ),
    docOut: (
        <>
            <path d="M4 3.5h8.5l3.5 3.5v13.5H4Z" />
            <path d="M12.5 3.5v3.5H16" />
            <path d="M6.3 16h5M9 13.5l2.5 2.5L9 18.5" />
        </>
    ),
    complaint: (
        <>
            <path d="M3 10a2 2 0 0 1 2-2h2l7-4v16l-7-4H5a2 2 0 0 1-2-2v-4Z" />
            <path d="M14 8.3a3.2 3.2 0 0 1 0 7.4" />
            <circle cx="19.3" cy="16.5" r="1.2" fill="#fff" stroke="none" />
        </>
    ),
};

const ACTIONS = [
    {
        key: 'purchase',
        title: 'Add Purchase',
        desc: 'Initiate a new vehicle or parts purchase.',
        icon: 'purchase',
        roles: ['Purchase_Manager'],
        target: { tab: 'Purchase_Manager', sub: 'add_purchase' },
    },
    {
        key: 'sale',
        title: 'Add Sale',
        desc: 'Log a new direct or commission sale.',
        icon: 'sale',
        roles: ['Salesman'],
        target: { tab: 'Salesman', sub: null },
    },
    {
        key: 'income',
        title: 'Add eto Income',
        desc: 'Record other income sources (e.g., etc.).',
        icon: 'income',
        roles: ['Accountant', 'ETO_Manager', 'ETO'],
        target: { tab: 'ETO', sub: 'add_case' }, // 👈 ETO tab target
    },
    {
        key: 'doc_in',
        title: 'Add Document Receiving',
        desc: 'Register incoming vehicle or customer documents.',
        icon: 'docIn',
        roles: ['Doc_Department', 'ETO_Manager'],
        target: { tab: null, sub: null },
    },
    {
        key: 'doc_out',
        title: 'Add Document Issuance',
        desc: 'Process outgoing vehicle or registration documents.',
        icon: 'docOut',
        roles: ['Doc_Department', 'ETO_Manager'],
        target: { tab: null, sub: null },
    },
    {
        key: 'complaint',
        title: 'Add Complaint',
        desc: 'Create a new customer or operational complaint ticket.',
        icon: 'complaint',
        roles: ['Customer_Care'],
        target: { tab: 'Customer_Care', sub: 'create' },
    },
];

const DashboardHome = ({ rolesArray = [], userName = '', onNavigate }) => {
    const hasAccess = (roles) => roles.some((r) => rolesArray.includes(r));

    const handleClick = (action) => {
        if (!hasAccess(action.roles)) return; 
        const tab = action.target.tab || action.roles.find((r) => rolesArray.includes(r));
        // Parent ko 'sub' pass na ho tab bhi ETO page add form khol sake, iske liye flag set karte hain
        if (action.target.sub === 'add_case') {
            try { sessionStorage.setItem('eto_open_add_case', String(Date.now())); } catch (e) {}
        }
        if (onNavigate) onNavigate(tab, action.target.sub);
    };

    return (
        <div>
            <style>{`
                .qa-card.enabled:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 14px 28px rgba(15, 23, 42, 0.08);
                    border-color: ${ACCENT}55;
                }
                .qa-card { transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease; }
            `}</style>

            <div style={styles.welcomeRow}>
                <div style={styles.welcomeEyebrow}>
                    Welcome back{userName ? `, ${userName.split(' ')[0]}` : ''} 👋
                </div>
                <div style={styles.welcomeTitle}>Here's a quick way to get things done</div>
            </div>

            <div style={styles.panel}>
                <div style={styles.panelHeader}>Quick Action</div>

                <div style={styles.grid}>
                    {ACTIONS.map((action) => {
                        const allowed = hasAccess(action.roles);
                        return (
                            <div
                                key={action.key}
                                className={`qa-card${allowed ? ' enabled' : ''}`}
                                onClick={() => handleClick(action)}
                                title={allowed ? '' : "You don't have permission for this action"}
                                style={{
                                    ...styles.card,
                                    cursor: allowed ? 'pointer' : 'not-allowed',
                                    opacity: allowed ? 1 : 0.55,
                                }}
                            >
                                <div
                                    style={{
                                        ...styles.iconBox,
                                        background: allowed ? ACCENT : '#94a3b8',
                                        boxShadow: allowed ? `0 10px 20px ${ACCENT}40` : 'none',
                                    }}
                                >
                                    <Icon>{ICONS[action.icon]}</Icon>
                                </div>
                                <div style={styles.textWrap}>
                                    <div style={styles.cardTitle}>{action.title}</div>
                                    <div style={styles.cardDesc}>
                                        {allowed ? action.desc : '🔒 No permission for this action'}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const styles = {
    welcomeRow: { marginBottom: '18px' },
    welcomeEyebrow: { fontSize: '13px', color: '#64748b', fontWeight: '700', marginBottom: '4px' },
    welcomeTitle: { fontSize: '20px', fontWeight: '800', color: '#1e293b' },
    panel: {
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        overflow: 'hidden',
    },
    panelHeader: {
        background: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
        padding: '16px 24px',
        fontWeight: '800',
        fontSize: '15px',
        color: '#1e293b',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '18px',
        padding: '24px',
    },
    card: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '16px',
        padding: '18px',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        background: '#fff',
    },
    iconBox: {
        width: '52px',
        height: '52px',
        minWidth: '52px',
        borderRadius: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    textWrap: { flex: 1, minWidth: 0 },
    cardTitle: { fontWeight: '800', fontSize: '15px', color: '#1e293b', marginBottom: '4px' },
    cardDesc: { fontSize: '12.5px', color: '#64748b', lineHeight: 1.5 },
};

export default DashboardHome;